import { useMemo, type KeyboardEvent, type RefObject } from "react";
import type { ExitRef } from "../exit-slots";
import type { Definition } from "../types";
import { edgeId, TRIGGER_TILE, targetPoint, type Box, type CanvasEdge, type Point } from "./canvas-layout";
import type { ConnectorApi, DropView } from "./connector-context";
import { handlePoint, type TileRow } from "./connector-geometry";
import { connectTarget, type ConnectCheck } from "./connector-rules";
import { useConnections } from "./use-connections";
import { useConnectorDrag, type PointerBindings } from "./use-connector-drag";

interface Options {
  definition: Definition;
  /** The viewer may look and jump to paths, not change connections. */
  readOnly: boolean;
  boxes: Readonly<Record<string, Box>>;
  rows: Readonly<Record<string, readonly TileRow[]>>;
  edges: readonly CanvasEdge[];
  zoom: number;
  viewport: RefObject<HTMLDivElement | null>;
  scrollToBox: (box: Box) => void;
  onChange: (definition: Definition) => void;
  place: (id: string, offset: Point) => void;
}

const refOf = (edge: CanvasEdge): ExitRef | null => (edge.key === null ? null : { path: edge.from, index: edge.step, key: edge.key });
const isTypingTarget = (target: EventTarget): boolean =>
  target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

/**
 * Everything the canvas does with connectors, in one place: the exit handles' context, the drag with its preview line and
 * the outlines of the tiles it can end on, the selected connector, and the keys (Del or Backspace deletes, Esc deselects).
 */
export function useCanvasConnectors({ definition, readOnly, boxes, rows, edges, zoom, viewport, scrollToBox, onChange, place }: Options) {
  const editable = !readOnly;
  const connections = useConnections({ definition, editable, boxes, viewport, scrollToBox, onChange, place });
  const dragger = useConnectorDrag({ viewport, zoom, boxes, editable, onStart: connections.onDragStart, onDrop: connections.drop, onCancel: connections.onDragCancel });
  const session = dragger.session;
  const source = session?.source;
  const pathIds = useMemo(() => Object.keys(definition.paths).sort(), [definition]);
  const existing = (id: string | null): string | null => (edges.some((edge) => edge.id === id) ? id : null);
  const selectedEdge = existing(connections.selected);
  const focusedEdge = existing(connections.focused);

  const checks = useMemo((): Record<string, ConnectCheck> | null => {
    if (!source) return null;
    return Object.fromEntries([TRIGGER_TILE, ...pathIds].map((id) => [id, connectTarget(definition, source, id)]));
  }, [definition, pathIds, source]);
  const dropView = (tileId: string): DropView | null => {
    const check = checks?.[tileId];
    if (!check) return null;
    return check.ok ? { kind: "valid", over: session?.over === tileId } : { kind: "invalid", reason: check.reason };
  };

  const api: ConnectorApi = {
    editable,
    pathIds,
    hasPath: (id) => id in definition.paths,
    pathTitle: connections.pathTitle,
    pathChip: connections.pathChip,
    exitName: connections.nameOf,
    selectedEdge,
    menuFor: connections.menuFor,
    highlight: connections.highlight,
    dropView,
    check: (ref, tileId) => connectTarget(definition, ref, tileId),
    bind: dragger.bind,
    takeSwallowedClick: dragger.takeSwallowedClick,
    select: connections.select,
    focus: connections.focus,
    openMenu: connections.openMenu,
    connect: connections.connect,
    disconnect: connections.disconnect,
    createPath: (ref) => { connections.createPath(ref); },
    goTo: connections.goTo,
  };

  /** The line that follows the pointer: from the exit's handle, snapping to the tile it would connect to. */
  const preview = ((): { from: Point; to: Point } | null => {
    if (!session) return null;
    const box = boxes[session.source.path];
    const from = box ? handlePoint(box, rows[session.source.path] ?? [], session.source.index, session.source.key) : null;
    const over = session.over ? boxes[session.over] : undefined;
    const valid = session.over !== null && checks?.[session.over]?.ok === true;
    return from ? { from, to: over && valid ? targetPoint(over) : session.pointer } : null;
  })();

  const grip = (edge: CanvasEdge): PointerBindings => {
    const ref = refOf(edge);
    if (!ref) throw new Error("The trigger's line has no grip.");
    return dragger.bind(ref, "grip");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isTypingTarget(event.target)) return;
    if (event.key === "Escape") connections.select(null);
    // On the canvas itself: the handles and the menu take Del and Backspace for their own exit.
    if (event.target !== event.currentTarget || !editable || !selectedEdge) return;
    if (event.key !== "Delete" && event.key !== "Backspace") return;
    const edge = edges.find((candidate) => candidate.id === selectedEdge);
    const ref = edge && refOf(edge);
    if (!ref) return;
    event.preventDefault();
    connections.disconnect(ref);
  };

  return {
    api,
    selectedEdge,
    focusedEdge,
    announcement: connections.announcement,
    preview,
    dimmed: session?.origin === "grip" ? edgeId(session.source.path, session.source.index, session.source.key) : null,
    grip,
    onKeyDown,
    onSelectEdge: (edge: CanvasEdge) => {
      connections.select(edge.id);
      connections.openMenu(edge.id);
    },
  };
}
